import { useLanguage } from "../../../contexts/LanguageContext";
import {
  CloudArrowUpIcon,
  ChevronDownIcon,
  PlusCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Tooltip from "../../../components/Tooltip";
import NumberInput from "../../../components/NumberInput";
import { useEffect, useMemo } from "react";
import {
  calculateDynamicExpirationDate,
  formatDate,
} from "../../../services/dateService";
import {
  validateMilestones,
  usesMilestoneRewards,
  newMilestoneId,
  sortMilestonesByPosition,
} from "../../../utils/passUtils";
import RewardJourneyBuilder from "./RewardJourneyBuilder";
import {
  tierThresholdMissing,
  couponRewardErrors,
} from "../passEditorHelpers";

// The card types that carry an initial bonus, by internal type string. The names
// on screen do not match these: `cashback` is the "Point" card and
// `cashback_money` is the "Cashback" one. Discount and membership are absent
// deliberately — they have no initial bonus for a conversion to copy.
const INITIAL_BONUS_TYPES = ["stamp", "cashback", "cashback_money", "balance"];

export default function PassForm({
  formData,
  setFormData,
  isSaving,
  handleSaveLayout,
  handleBackgroundChange,
  handleStampIconChange,
  isAdvancedSettingsOpen,
  setIsAdvancedSettingsOpen,
  isLayoutOrderOpen,
  setIsLayoutOrderOpen,
  passes,
  useCustomTerms,
  setUseCustomTerms,
  termsUrl,
  setTermsUrl,
  setIsTermsModalOpen,
  selectedStore,
  isEditMode,
  allowTypeChange,
  onMilestoneImageSelect,
  saveAttempted = false,
}) {
  const { t } = useLanguage();

  // saveAttempted is owned by the page, not this form. It gates inline milestone
  // errors so a fresh card — which seeds an empty-label milestone that is
  // "invalid" from the first render — doesn't paint a red "reward name required"
  // before a single keystroke.
  //
  // It lives up in handleSaveLayout because there are two save buttons: this
  // form's own, and the one in the page header. While the flag was local state
  // here, only this one could set it, so saving from the header blocked with a
  // message and highlighted nothing.
  //
  // Gated on the same usesMilestoneRewards predicate the save path uses, so the
  // fields painted red are exactly the ones that block the save — skipping the
  // array walk for every other card type, and recomputing only when the
  // milestones actually change (not on every keystroke in unrelated fields).
  const milestoneRewardsActive = usesMilestoneRewards(formData);
  const milestoneErrors = useMemo(
    () =>
      saveAttempted && milestoneRewardsActive
        ? validateMilestones(formData.milestones).errors
        : {},
    [saveAttempted, milestoneRewardsActive, formData.milestones],
  );

  // Blank reward label/value on a coupon, and a next tier with no threshold:
  // red field + message, on the same predicates the save path blocks on.
  // Gated on saveAttempted like the milestones — picking a target seeds an
  // empty threshold on Point/Discount cards, so an ungated check painted the
  // field red before the merchant had a chance to type. The save attempt is
  // what turns the empty field into a problem to show.
  const couponErrors = saveAttempted ? couponRewardErrors(formData) : {};
  const tierThresholdError = saveAttempted && tierThresholdMissing(formData);

  // Which wording the coupon's conversion toggle should use, or null to hide it:
  // the type of the card this coupon converts into, when that card has an
  // initial bonus at all.
  const conversionBonusType = useMemo(() => {
    if (!formData.targetPassId || formData.targetPassId === "none") return null;

    const target = passes?.find(
      (pass) => String(pass.id) === String(formData.targetPassId),
    );
    const type = target?.pass?.type;

    return INITIAL_BONUS_TYPES.includes(type) ? type : null;
  }, [formData.targetPassId, passes]);

  // Stamp cards are always milestone-driven: the card length is derived from the
  // furthest reward rather than entered directly. A plain single-reward card is
  // just one milestone at the required count.
  //
  // Milestones are kept sorted by position here — the single chokepoint every
  // add/update/remove flows through — so the rendered order always matches stamp
  // progression: the "Final Reward" (highest position) is the last card and the
  // "Milestone N" numbering ascends. (Cards are keyed by _uid and edits target
  // by _uid, so re-sorting never retargets the wrong milestone.)
  const applyMilestones = (milestones) => {
    const sorted = sortMilestonesByPosition(milestones);
    const maxPosition = sorted.reduce(
      (max, m) => Math.max(max, Number(m.position) || 0),
      0,
    );

    setFormData((prev) => ({
      ...prev,
      milestones: sorted,
      stampCount: maxPosition || prev.stampCount,
    }));
  };

  const updateMilestone = (id, patch) => {
    applyMilestones(
      formData.milestones.map((m) => (m._uid === id ? { ...m, ...patch } : m)),
    );
  };

  const addMilestone = () => {
    const ms = formData.milestones || [];
    if (ms.length === 0) {
      applyMilestones([
        {
          position: Number(formData.stampCount) || 8,
          label: "",
          _uid: newMilestoneId(),
        },
      ]);
      return;
    }

    // The Final Reward (highest position) must stay pinned at the bottom, so a
    // new reward always lands *below* it. Drop it in the widest gap under the
    // final position.
    const finalPosition = ms.reduce(
      (max, m) => Math.max(max, Number(m.position) || 0),
      0,
    );
    const positions = ms
      .map((m) => Number(m.position) || 0)
      .sort((a, b) => a - b);

    let newPosition = null;
    let widestGap = 0;
    const boundaries = [0, ...positions];
    for (let i = 1; i < boundaries.length; i++) {
      const lo = boundaries[i - 1];
      const hi = boundaries[i];
      if (hi > finalPosition) break; // never consider slots at/above the final
      const mid = Math.floor((lo + hi) / 2);
      if (mid > lo && mid < hi && hi - lo >= widestGap) {
        widestGap = hi - lo;
        newPosition = mid;
      }
    }

    if (newPosition == null) {
      // Every stamp below the final is taken: grow the card by one (the final
      // moves up) and slot the new reward into the old final position, so the
      // same Final Reward stays last.
      applyMilestones([
        ...ms.map((m) =>
          Number(m.position) === finalPosition
            ? { ...m, position: finalPosition + 1 }
            : m,
        ),
        { position: finalPosition, label: "", _uid: newMilestoneId() },
      ]);
      return;
    }

    applyMilestones([
      ...ms,
      { position: newPosition, label: "", _uid: newMilestoneId() },
    ]);
  };

  const removeMilestone = (id) => {
    applyMilestones(formData.milestones.filter((m) => m._uid !== id));
  };

  const changeType = (type) => {
    // Header field is not shown on Google Wallet, so the primary metric defaults
    // to the right field (visible on both platforms) and the header is left empty.
    let defaultData = {
      leftField: "member",
      headerField: "none",
      enableTier: false,
      // Same reason the tier checkbox clears these: enableTier alone only hides
      // the fields. A ladder is between cards of ONE type (the target selector
      // filters on it), so a target chosen for the old type is meaningless
      // here — and leaving it behind builds an upgrade campaign that promotes
      // holders onto a card of the wrong type.
      targetPassId: "none",
      targetAmount: "",
    };

    if (type === "cashback") {
      setFormData((prev) => ({
        ...prev,
        rightField: "points",
        initialPoints: 200,
        ...defaultData,
      }));
    }

    if (type === "cashback_money") {
      setFormData((prev) => ({
        ...prev,
        rightField: "balance_money",
        initialPoints: 0,
        ...defaultData,
      }));
    }

    if (type === "membership") {
      setFormData((prev) => ({
        ...prev,
        rightField: "visits",
        initialPoints: 0,
        ...defaultData,
      }));
    }

    if (type === "balance") {
      setFormData((prev) => ({
        ...prev,
        rightField: "balance",
        initialPoints: 0,
        ...defaultData,
      }));
    }

    if (type === "discount") {
      setFormData((prev) => ({
        ...prev,
        rightField: "discount",
        discount: 5,
        initialPoints: 0,
        ...defaultData,
      }));
    }

    if (type === "stamp") {
      setFormData((prev) => {
        // Stamp cards are always milestone-driven; guarantee at least one
        // milestone (a plain single reward at the required count) when the
        // card is switched to this type.
        const hasMilestones =
          Array.isArray(prev.milestones) && prev.milestones.length > 0;
        return {
          ...prev,
          rightField: "rewards",
          initialPoints: 2,
          stampRewardMode: "milestone",
          milestones: hasMilestones
            ? prev.milestones
            : [
                {
                  position: Number(prev.stampCount) || 8,
                  label: "",
                  _uid: newMilestoneId(),
                },
              ],
          ...defaultData,
        };
      });
    }

    if (type === "coupon") {
      setFormData((prev) => ({
        ...prev,
        headerField: "none",
        initialPoints: 1,
        ...defaultData,
        rightField: formData.campaign.label,
        preview: {
          ...prev.preview,
          rightField: {
            label: formData.campaign.label,
            value: formData.campaign.value,
          },
        },
      }));
    }
  };

  useEffect(() => {
    if (formData.validUntil) {
      let newDate = formData.validUntil;
      if (formData.passExpirationType === "dynamic") {
        newDate = calculateDynamicExpirationDate({
          value: formData.passExpirationDynamic || 1,
          unit: formData.passExpirationDynamicUnit,
        });
      }

      setFormData((prev) => ({
        ...prev,
        formattedValidUntil: formatDate(newDate, formData.validUntilDateFormat),
        validUntil: newDate,
      }));
    }

    if (formData.type === "coupon") {
      formData.preview.rightField = {
        label: formData.campaign.label,
        value: formData.campaign.value,
      };
    }

    const fields = ["headerField", "leftField", "rightField"];

    if (formData.enablePassExpiration) {
      fields.forEach((field) => {
        if (formData[field] == "none") {
          formData[field] = "valid_until";
        }
      });
    } else {
      fields.forEach((field) => {
        if (formData[field] == "valid_until") {
          formData[field] = "none";
        }
      });
    }

    if (formData.enableTier) {
      fields.forEach((field) => {
        if (formData[field] == "none") {
          formData[field] = "tier";
        }
      });
    } else {
      fields.forEach((field) => {
        if (formData[field] == "tier") {
          formData[field] = "none";
        }
      });
    }
  }, [
    formData.type,
    formData.validUntil,
    formData.campaign.label,
    formData.enablePassExpiration,
    formData.enableTier,
    formData.passExpirationType,
    formData.passExpirationDynamic,
    formData.passExpirationDynamicUnit,
  ]);

  return (
    <div className="w-full">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {t("passes.form.basicInfo")}
          </h2>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("passes.form.name.label")}
              </label>
              <input
                type="text"
                placeholder={t("passes.form.name.placeholder")}
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            {/* Pass Type */}
            <div
              className={`${isEditMode && !allowTypeChange ? "hidden" : ""}`}
            >
              <label className="block text-sm font-medium text-gray-700">
                {t("passes.form.passType.label")}
              </label>
              <select
                value={formData.type}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }));
                  changeType(e.target.value);
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option key="stamp" value="stamp">
                  {t("passes.form.passType.stamp")}
                </option>
                <option key="cashback" value="cashback">
                  {t("passes.form.passType.cashback")}
                </option>
                <option key="cashback_money" value="cashback_money">
                  {t("passes.form.passType.cashback_money")}
                </option>
                <option key="balance" value="balance">
                  {t("passes.form.passType.balance")}
                </option>
                <option key="discount" value="discount">
                  {t("passes.form.passType.discount")}
                </option>
                <option key="membership" value="membership">
                  {t("passes.form.passType.membership")}
                </option>
                <option key="coupon" value="coupon">
                  {t("passes.form.passType.coupon")}
                </option>
              </select>
            </div>

            {/* Appearance */}
            <div className="border border-gray-300 rounded-md p-4">
              <h2 className="text-m text-gray-900 mb-4">
                {" "}
                {t("passes.form.appearance")}{" "}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Colors */}
                <div className="grid">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t("passes.form.colors.background")}
                    </label>
                    <input
                      type="color"
                      value={formData.backgroundColor}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          backgroundColor: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full h-10 rounded-md border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t("passes.form.colors.text")}
                    </label>
                    <input
                      type="color"
                      value={formData.textColor}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          textColor: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full h-10 rounded-md border-gray-300"
                    />
                  </div>
                </div>

                {/* Background */}
                <div className="grid">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("passes.form.background.label")}
                  </label>
                  <div
                    onClick={() =>
                      document.getElementById("background-upload").click()
                    }
                    className="mt-1 flex justify-center pt-2 pb-1 border-2 border-dashed rounded-md cursor-pointer hover:border-gray-400 border-gray-300"
                  >
                    <div className="space-y-1 text-center">
                      {formData.backgroundImage ? (
                        <div>
                          <img
                            src={formData.backgroundImage}
                            alt={t("passes.form.background.preview")}
                            className="mx-auto h-24 w-full object-cover rounded-md"
                          />
                          <div className="mt-2">
                            <span className="relative font-medium text-sm text-indigo-600 hover:text-indigo-500">
                              {t("passes.form.background.change")}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <div className="flex text-sm text-gray-600 justify-center">
                            <span className="relative font-medium text-indigo-600 hover:text-indigo-500">
                              {t("passes.form.background.upload")}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {t("passes.form.background.formats")}
                          </p>
                        </>
                      )}
                      <input
                        id="background-upload"
                        name="background-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleBackgroundChange}
                        className="sr-only"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stamp Icon */}
            {formData.type === "stamp" && (
              <div className="border border-gray-300 rounded-md p-4">
                <label className="block text-m font-medium text-gray-700 mb-4">
                  {t("passes.form.stampIcon.label")}
                </label>

                <RewardJourneyBuilder
                  milestones={formData.milestones || []}
                  stampCount={formData.stampCount}
                  stampIcon={formData.stampIcon}
                  stampIconColor={formData.stampIconColor}
                  initialPoints={formData.initialPoints}
                  milestoneErrors={milestoneErrors}
                  onSetInitialPoints={(value) =>
                    setFormData((prev) => ({ ...prev, initialPoints: value }))
                  }
                  onUpdateMilestone={updateMilestone}
                  onAddMilestone={addMilestone}
                  onRemoveMilestone={removeMilestone}
                  onSetStampIcon={(url) =>
                    setFormData((prev) => ({ ...prev, stampIcon: url }))
                  }
                  onSetStampIconColor={(color) =>
                    setFormData((prev) => ({
                      ...prev,
                      stampIconColor: color,
                    }))
                  }
                  onUploadStampIcon={handleStampIconChange}
                  onUploadMilestoneIcon={onMilestoneImageSelect}
                />
              </div>
            )}

            {/* Coupon */}
            {formData.type === "coupon" && (
              <div>
                <div className="sm:flex sm:gap-4 mt-4">
                  <div className="flex-1 mb-4 sm:mb-0">
                    <div className="flex items-center">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("passes.form.campaign.label")}
                      </label>
                    </div>
                    <input
                      type="text"
                      value={formData.campaign.label}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          campaign: {
                            ...prev.campaign,
                            label: e.target.value,
                          },
                          rightField: e.target.value,
                        }));
                      }}
                      className={`mt-1 block w-full rounded-md shadow-xs ${
                        couponErrors.label ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {couponErrors.label && (
                      <p className="mt-1 text-xs text-red-600">
                        {t("passes.form.campaign.labelRequired")}
                      </p>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("passes.form.campaign.value")}
                      </label>
                    </div>
                    <input
                      type="text"
                      value={formData.campaign.value}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          campaign: {
                            ...prev.campaign,
                            value: e.target.value,
                          },
                          preview: {
                            ...prev.preview,
                            rightField: {
                              ...prev.preview.rightField,
                              value: e.target.value,
                            },
                          },
                        }));
                      }}
                      className={`mt-1 block w-full rounded-md shadow-xs ${
                        couponErrors.value ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {couponErrors.value && (
                      <p className="mt-1 text-xs text-red-600">
                        {t("passes.form.campaign.valueRequired")}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex-1 mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("passes.form.tier.targetAfterCouponUsed")}
                  </label>
                  <select
                    value={formData.targetPassId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        targetPassId: e.target.value,
                        targetAmount: 1,
                        // The bonus is consent for one specific target card, so
                        // it does not survive a change of target. Without this
                        // the flag stayed on while the checkbox below was
                        // hidden (a membership or discount target has no
                        // initial bonus to copy) — the merchant saw the setting
                        // disappear, and it was still saved as on, ready to
                        // apply itself the moment a bonus-carrying target was
                        // picked again.
                        applyInitialBonusOnConversion: false,
                      }))
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                  >
                    <option value="none">
                      {t("passes.form.tier.targetPassNone")}
                    </option>
                    {passes
                      ?.filter(
                        (pass) =>
                          pass.id != formData.id && pass.pass.type !== "coupon",
                      )
                      .map((pass) => (
                        <option key={pass.id} value={pass.id}>
                          {pass.pass.name} - {pass.pass.type}
                        </option>
                      ))}
                  </select>

                  {/* Shown only when the chosen target actually has an initial
                      bonus to copy. Its label mirrors that card's own wording —
                      the merchant goes looking for this setting under the name
                      the target card gives it, and the four types each use a
                      different one (stamps / points / welcome bonus / credit).
                      Discount and membership targets have no such setting, so
                      the whole control stays hidden for them. */}
                  {conversionBonusType && (
                    <div className="mt-3 flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="applyInitialBonusOnConversion"
                        checked={!!formData.applyInitialBonusOnConversion}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            applyInitialBonusOnConversion: e.target.checked,
                          }))
                        }
                        className="mt-0.5 rounded-md border-gray-300 shadow-xs"
                      />
                      <div>
                        <label
                          htmlFor="applyInitialBonusOnConversion"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t(
                            `passes.form.tier.applyInitialBonusOnConversion.${conversionBonusType}`,
                          )}
                        </label>
                        <p className="text-sm text-gray-500">
                          {t(
                            "passes.form.tier.applyInitialBonusOnConversion.description",
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cashback */}
            {formData.type === "cashback" && (
              <div>
                <div className="flex items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("passes.form.initialPoints.label")}
                  </label>
                  <Tooltip
                    text={t("passes.form.initialPoints.tooltip")}
                    className="ml-1"
                  />
                </div>
                <input
                  type="number"
                  min="0"
                  value={formData.initialPoints}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      initialPoints: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                />
              </div>
            )}

            {/* Cashback Money */}
            {formData.type === "cashback_money" && (
              <div>
                <div className="flex items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("passes.form.initialCashbackMoney.label")}
                  </label>
                  <Tooltip
                    text={t("passes.form.initialCashbackMoney.tooltip")}
                    className="ml-1"
                  />
                </div>
                <input
                  type="number"
                  min="0"
                  value={formData.initialPoints}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      initialPoints: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                />
              </div>
            )}

            {/* Balance */}
            {formData.type === "balance" && (
              <div>
                <div className="flex items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("passes.form.initialBalance.label")}
                  </label>
                  <Tooltip
                    text={t("passes.form.initialBalance.tooltip")}
                    className="ml-1 z-50"
                  />
                </div>
                <input
                  type="number"
                  min="0"
                  value={formData.initialPoints}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      initialPoints: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                />
              </div>
            )}

            {/* Discount */}
            {formData.type === "discount" && (
              <div>
                <div className="flex items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("passes.form.discount.label")}
                  </label>
                  <Tooltip
                    text={t("passes.form.discount.tooltip")}
                    className="ml-1 z-50"
                  />
                </div>
                <div className="relative mt-1">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount}
                    onChange={(e) => {
                      const value = Math.min(100, Math.max(0, e.target.value));
                      setFormData((prev) => ({ ...prev, discount: value }));
                    }}
                    className="block w-full pr-8 rounded-md border-gray-300 shadow-xs"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Settings */}
            <div className="border border-gray-300 rounded-md p-4">
              <button
                type="button"
                onClick={() =>
                  setIsAdvancedSettingsOpen(!isAdvancedSettingsOpen)
                }
                className="flex w-full items-center justify-between text-lg font-medium text-gray-900"
              >
                <span>{t("passes.form.advanced_settings.title")}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 transform transition-transform ${isAdvancedSettingsOpen ? "rotate-180" : ""}`}
                />
              </button>

              <div
                className={`transition-all duration-200 ease-in-out ${isAdvancedSettingsOpen ? "block" : "hidden"}`}
              >
                <div
                  className={`overflow-visible ${isAdvancedSettingsOpen ? "mt-4" : ""}`}
                >
                  {/* Expiration Pass Expiration */}
                  <div className="p-4 border border-gray-300 rounded-md mb-4">
                    <div
                      className={`flex items-center gap-2 ${formData.enablePassExpiration ? "mb-4" : ""}`}
                    >
                      <input
                        type="checkbox"
                        id="enablePassExpiration"
                        checked={formData.enablePassExpiration}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            enablePassExpiration: e.target.checked,
                          }))
                        }
                        className="rounded-md border-gray-300 shadow-xs"
                      />
                      <label
                        htmlFor="enablePassExpiration"
                        className="block text-sm font-medium text-gray-700"
                      >
                        {t("passes.form.enablePassExpiration.label")}
                      </label>
                    </div>
                    <div>
                      {formData.enablePassExpiration && (
                        <div className="sm:flex sm:gap-4">
                          <div className="flex-1 mb-4 sm:mb-0">
                            <div className="flex items-center">
                              <label className="block text-sm font-medium text-gray-700">
                                {t("passes.form.expiration.label")}
                              </label>
                              <Tooltip
                                text={t("passes.form.expiration.tooltip")}
                                className="ml-1"
                              />
                            </div>
                            <select
                              name="pass_expiration_type"
                              id="pass_expiration_type"
                              value={formData.passExpirationType}
                              onChange={(e) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  passExpirationType: e.target.value,
                                }));
                              }}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                            >
                              <option value="fixed">
                                {t("passes.form.expiration.fixed")}
                              </option>
                              <option value="dynamic">
                                {t("passes.form.expiration.dynamic")}
                              </option>
                            </select>
                          </div>

                          {formData.passExpirationType === "fixed" && (
                            <div className="flex-1">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {t("passes.form.valid_until.label")}
                                </label>
                                <Tooltip
                                  text={t("passes.form.valid_until.tooltip")}
                                  className="ml-1"
                                />
                              </div>
                              <input
                                type="date"
                                value={formData.validUntil}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    validUntil: e.target.value,
                                  }));
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                              />
                            </div>
                          )}

                          {formData.passExpirationType === "dynamic" && (
                            <div className="flex gap-4">
                              <div className="flex-1">
                                <div className="flex items-center">
                                  <label className="block text-sm font-medium text-gray-700">
                                    {t("passes.form.term.label")}
                                  </label>
                                  <Tooltip
                                    text={t("passes.form.term.tooltip")}
                                    className="ml-1"
                                  />
                                </div>
                                <input
                                  type="number"
                                  value={formData.passExpirationDynamic}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      passExpirationDynamic: parseInt(
                                        e.target.value,
                                      ),
                                    }))
                                  }
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                />
                              </div>

                              <div className="flex-1">
                                <div className="flex items-center">
                                  <label className="block text-sm font-medium text-gray-700">
                                    {t("passes.form.period.label")}
                                  </label>
                                  <Tooltip
                                    text={t("passes.form.period.tooltip")}
                                    className="ml-1"
                                  />
                                </div>
                                <select
                                  value={formData.passExpirationDynamicUnit}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      passExpirationDynamicUnit: e.target.value,
                                    }))
                                  }
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                >
                                  <option key="days" value="days">
                                    {t("passes.form.expiration.days")}
                                  </option>
                                  <option key="weeks" value="weeks">
                                    {t("passes.form.expiration.weeks")}
                                  </option>
                                  <option key="months" value="months">
                                    {t("passes.form.expiration.months")}
                                  </option>
                                  <option key="years" value="years">
                                    {t("passes.form.expiration.years")}
                                  </option>
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expiration Points Expiration */}
                  {["stamp", "balance", "cashback", "cashback_money"].includes(
                    formData.type,
                  ) && (
                    <div className="p-4 border border-gray-300 rounded-md mb-4">
                      <div
                        className={`flex items-center gap-2 ${formData.enablePointsExpiration ? "mb-4" : ""}`}
                      >
                        <input
                          type="checkbox"
                          id="enablePointsExpiration"
                          checked={formData.enablePointsExpiration}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              enablePointsExpiration: e.target.checked,
                            }))
                          }
                          className="rounded-md border-gray-300 shadow-xs"
                        />
                        <label
                          htmlFor="enablePointsExpiration"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {formData.type === "balance" &&
                            t("passes.form.enableBalanceExpiration.label")}
                          {formData.type === "cashback" &&
                            t("passes.form.enablePointsExpiration.label")}
                          {formData.type === "stamp" &&
                            t("passes.form.enableStampsExpiration.label")}
                          {formData.type === "cashback_money" &&
                            t(
                              "passes.form.enableCashbackMoneyExpiration.label",
                            )}
                        </label>
                        {/* One tooltip for all four card types: the rule it
                            states — the setting binds at earn time and never
                            reaches back — is the same whatever the unit. */}
                        <Tooltip
                          text={t("passes.form.enablePointsExpiration.tooltip")}
                        />
                      </div>
                      <div>
                        {formData.enablePointsExpiration && (
                          <div className="sm:flex sm:gap-4">
                            <div className="flex-1 mb-4 sm:mb-0">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {formData.type === "balance" &&
                                    t(
                                      "passes.form.balance_expiration_type.label",
                                    )}
                                  {formData.type === "cashback" &&
                                    t(
                                      "passes.form.points_expiration_type.label",
                                    )}
                                  {formData.type === "stamp" &&
                                    t(
                                      "passes.form.stamps_expiration_type.label",
                                    )}
                                  {formData.type === "cashback_money" &&
                                    t(
                                      "passes.form.cashback_money_expiration_type.label",
                                    )}
                                </label>
                                <Tooltip
                                  text={t(
                                    "passes.form.points_expiration_type.tooltip",
                                  )}
                                  className="ml-1"
                                />
                              </div>
                              <select
                                name="points_expiration_type"
                                id="points_expiration_type"
                                value={formData.pointsExpirationType}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    pointsExpirationType: e.target.value,
                                  }));
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                              >
                                <option value="dynamic">
                                  {formData.type === "balance" &&
                                    t("passes.form.balance_expiration.dynamic")}
                                  {formData.type === "cashback" &&
                                    t("passes.form.points_expiration.dynamic")}
                                  {formData.type === "stamp" &&
                                    t("passes.form.stamps_expiration.dynamic")}
                                  {formData.type === "cashback_money" &&
                                    t(
                                      "passes.form.cashback_money_expiration.dynamic",
                                    )}
                                </option>
                                <option value="fixed">
                                  {t("passes.form.expiration.fixed")}
                                </option>
                              </select>
                            </div>
                            <div className="flex-1">
                              {formData.pointsExpirationType === "fixed" && (
                                <div className="flex-1">
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {formData.type === "balance" &&
                                        t(
                                          "passes.form.balance_valid_until.label",
                                        )}
                                      {formData.type === "cashback" &&
                                        t(
                                          "passes.form.points_valid_until.label",
                                        )}
                                      {formData.type === "stamp" &&
                                        t(
                                          "passes.form.stamps_valid_until.label",
                                        )}
                                      {formData.type === "cashback_money" &&
                                        t(
                                          "passes.form.cashback_money_valid_until.label",
                                        )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.points_valid_until.tooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>
                                  <input
                                    type="date"
                                    value={formData.pointsValidUntil}
                                    onChange={(e) => {
                                      setFormData((prev) => ({
                                        ...prev,
                                        pointsValidUntil: e.target.value,
                                      }));
                                    }}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </div>
                              )}

                              {formData.pointsExpirationType === "dynamic" && (
                                <div className="flex gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center">
                                      <label className="block text-sm font-medium text-gray-700">
                                        {t("passes.form.term.label")}
                                      </label>
                                      <Tooltip
                                        text={t("passes.form.term.tooltip")}
                                        className="ml-1"
                                      />
                                    </div>
                                    <input
                                      type="number"
                                      value={formData.pointsExpirationDynamic}
                                      onChange={(e) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          pointsExpirationDynamic: parseInt(
                                            e.target.value,
                                          ),
                                        }))
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                    />
                                  </div>

                                  <div className="flex-1">
                                    <div className="flex items-center">
                                      <label className="block text-sm font-medium text-gray-700">
                                        {t("passes.form.period.label")}
                                      </label>
                                      <Tooltip
                                        text={t("passes.form.period.tooltip")}
                                        className="ml-1"
                                      />
                                    </div>
                                    <select
                                      value={
                                        formData.pointsExpirationDynamicUnit
                                      }
                                      onChange={(e) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          pointsExpirationDynamicUnit:
                                            e.target.value,
                                        }))
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                    >
                                      <option key="days" value="days">
                                        {t("passes.form.expiration.days")}
                                      </option>
                                      <option key="weeks" value="weeks">
                                        {t("passes.form.expiration.weeks")}
                                      </option>
                                      <option key="months" value="months">
                                        {t("passes.form.expiration.months")}
                                      </option>
                                      <option key="years" value="years">
                                        {t("passes.form.expiration.years")}
                                      </option>
                                    </select>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Enable Points Restriction */}
                  {[
                    "stamp",
                    "balance",
                    "cashback",
                    "membership",
                    "cashback_money",
                  ].includes(formData.type) && (
                    <div className="p-4 border border-gray-300 rounded-md mb-4">
                      <div
                        className={`flex items-center gap-2 ${formData.enablePointsRestriction ? "mb-4" : ""}`}
                      >
                        <input
                          type="checkbox"
                          id="enablePointsRestriction"
                          checked={formData.enablePointsRestriction}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              enablePointsRestriction: e.target.checked,
                            }))
                          }
                          className="rounded-md border-gray-300 shadow-xs"
                        />
                        <label
                          htmlFor="enablePointsRestriction"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {formData.type === "balance" &&
                            t("passes.form.enableBalanceRestriction.label")}
                          {formData.type === "cashback" &&
                            t("passes.form.enablePointsRestriction.label")}
                          {formData.type === "stamp" &&
                            t("passes.form.enableStampsRestriction.label")}
                          {formData.type === "membership" &&
                            t("passes.form.enableMembershipRestriction.label")}
                          {formData.type === "cashback_money" &&
                            t(
                              "passes.form.enableCashbackMoneyRestriction.label",
                            )}
                        </label>
                      </div>
                      <div>
                        {formData.enablePointsRestriction && (
                          <div className="sm:flex sm:gap-4">
                            <div className="flex-1 mb-4 sm:mb-0">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {t("passes.form.restriction.period.label")}
                                </label>
                                <Tooltip
                                  text={t(
                                    "passes.form.restriction.period.tooltip",
                                  )}
                                  className="ml-1"
                                />
                              </div>
                              <select
                                name="points_restriction_type"
                                id="points_restriction_type"
                                value={formData.pointsRestrictionType}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    pointsRestrictionType: e.target.value,
                                  }));
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                              >
                                <option value="daily">
                                  {t("passes.form.restriction.period.daily")}
                                </option>
                                <option value="weekly">
                                  {t("passes.form.restriction.period.weekly")}
                                </option>
                                <option value="monthly">
                                  {t("passes.form.restriction.period.monthly")}
                                </option>
                              </select>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {formData.type === "balance"
                                    ? t("passes.form.restriction.redeem.label")
                                    : t("passes.form.restriction.scan.label")}
                                </label>
                                <Tooltip
                                  text={
                                    formData.type === "balance"
                                      ? t(
                                          "passes.form.restriction.redeem.tooltip",
                                        )
                                      : t(
                                          "passes.form.restriction.scan.tooltip",
                                        )
                                  }
                                  className="ml-1"
                                />
                              </div>
                              <input
                                type="number"
                                value={formData.pointsRestrictionValue}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    pointsRestrictionValue: parseInt(
                                      e.target.value,
                                    ),
                                  }));
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Layout Order */}
                  <div className="p-4 border border-gray-300 rounded-md mb-4">
                    <button
                      type="button"
                      onClick={() => setIsLayoutOrderOpen(!isLayoutOrderOpen)}
                      className="flex w-full items-center justify-between text-sm font-medium text-gray-900"
                    >
                      <span>{t("passes.form.layoutOrder.label")}</span>
                      <ChevronDownIcon
                        className={`h-5 w-5 transform transition-transform ${isLayoutOrderOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <div
                      className={`transition-all duration-200 ease-in-out ${isLayoutOrderOpen ? "block" : "hidden"}`}
                    >
                      <div
                        className={`overflow-visible ${isLayoutOrderOpen ? "mt-4" : ""}`}
                      >
                        <div>
                          {/* Header Field */}
                          <div className="flex-1">
                            <label
                              htmlFor="headerField"
                              className="block text-sm font-medium text-gray-700"
                            >
                              {t("passes.form.layoutOrder.headerField")}
                            </label>
                            <select
                              id="headerField"
                              value={formData.headerField}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  headerField: e.target.value,
                                }))
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                            >
                              <option key="none" value="none">
                                {t("passes.form.layoutOrder.leaveEmpty")}
                              </option>
                              {/* Header is a narrow numeric slot: the milestone
                                  REWARD text ("Free chips ready") overflows and
                                  truncates there, so stamp cards only offer the
                                  stamp count. The reward text stays available on
                                  the left/right fields. */}
                              {formData.type === "stamp" && (
                                <option key="stamps" value="stamps">
                                  {t("passes.form.layoutOrder.stamps")}
                                </option>
                              )}
                              {formData.type === "cashback" && (
                                <option key="points" value="points">
                                  {t("passes.form.layoutOrder.points")}
                                </option>
                              )}
                              {formData.type === "cashback_money" && (
                                <option
                                  key="balance_money"
                                  value="balance_money"
                                >
                                  {t("passes.form.layoutOrder.balance_money")}
                                </option>
                              )}
                              {formData.type === "balance" && (
                                <option key="balance" value="balance">
                                  {t("passes.form.layoutOrder.balance")}
                                </option>
                              )}
                              {formData.type === "membership" && (
                                <option key="visits" value="visits">
                                  {t("passes.form.layoutOrder.visits")}
                                </option>
                              )}
                              {formData.type === "discount" && (
                                <option key="discount" value="discount">
                                  {t("passes.form.layoutOrder.discount")}
                                </option>
                              )}
                              {formData.enableTier && (
                                <option key="tier" value="tier">
                                  {t("passes.form.layoutOrder.tier")}
                                </option>
                              )}
                              {formData.enablePassExpiration && (
                                <option key="valid_until" value="valid_until">
                                  {t("passes.form.layoutOrder.validUntil")}
                                </option>
                              )}
                              {selectedStore?.custom_fields?.map((field) => (
                                <option key={field.id} value={field.label}>
                                  {field.label.toUpperCase()}
                                </option>
                              ))}
                            </select>
                            {formData.headerField !== "none" && (
                              <p className="mt-1 text-xs text-amber-600">
                                {t("passes.form.preview.androidNoHeaderField")}
                              </p>
                            )}
                          </div>

                          {/* Left Field */}
                          <div className="flex-1 mt-2">
                            <label
                              htmlFor="leftField"
                              className="block text-sm font-medium text-gray-700"
                            >
                              {t("passes.form.layoutOrder.leftField")}
                            </label>
                            <select
                              id="leftField"
                              value={formData.leftField}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  leftField: e.target.value,
                                }))
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                            >
                              <option key="none" value="none">
                                {t("passes.form.layoutOrder.leaveEmpty")}
                              </option>
                              <option key="member" value="member">
                                {t("passes.form.layoutOrder.member")}
                              </option>
                              <option key="birthday" value="birthday">
                                {t("passes.form.layoutOrder.birthday")}
                              </option>
                              {formData.type === "stamp" && (
                                <>
                                  <option key="rewards" value="rewards">
                                    {t("passes.form.layoutOrder.rewards")}
                                  </option>
                                  <option key="stamps" value="stamps">
                                    {t("passes.form.layoutOrder.stamps")}
                                  </option>
                                </>
                              )}
                              {formData.type === "discount" && (
                                <option key="discount" value="discount">
                                  {t("passes.form.layoutOrder.discount")}
                                </option>
                              )}
                              {formData.type === "cashback" && (
                                <option key="points" value="points">
                                  {t("passes.form.layoutOrder.points")}
                                </option>
                              )}
                              {formData.type === "balance" && (
                                <option key="balance" value="balance">
                                  {t("passes.form.layoutOrder.balance")}
                                </option>
                              )}
                              {formData.type === "membership" && (
                                <option key="visits" value="visits">
                                  {t("passes.form.layoutOrder.visits")}
                                </option>
                              )}
                              {formData.type === "cashback_money" && (
                                <option
                                  key="balance_money"
                                  value="balance_money"
                                >
                                  {t("passes.form.layoutOrder.balance_money")}
                                </option>
                              )}

                              {formData.enableTier && (
                                <option key="tier" value="tier">
                                  {t("passes.form.layoutOrder.tier")}
                                </option>
                              )}
                              {formData.enablePassExpiration && (
                                <option key="valid_until" value="valid_until">
                                  {t("passes.form.layoutOrder.validUntil")}
                                </option>
                              )}
                              {selectedStore?.custom_fields?.map((field) => (
                                <option key={field.id} value={field.label}>
                                  {field.label.toUpperCase()}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Right Field */}
                          <div className="flex-1 mt-2">
                            <label
                              htmlFor="rightField"
                              className="block text-sm font-medium text-gray-700"
                            >
                              {t("passes.form.layoutOrder.rightField")}
                            </label>
                            <select
                              id="rightField"
                              value={formData.rightField}
                              disabled={formData.type === "coupon"}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  rightField: e.target.value,
                                }))
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                            >
                              {formData.type == "coupon" && (
                                <option key="coupon" value="coupon">
                                  {formData.campaign.label}
                                </option>
                              )}
                              <option key="none" value="none">
                                {t("passes.form.layoutOrder.leaveEmpty")}
                              </option>
                              <option key="member" value="member">
                                {t("passes.form.layoutOrder.member")}
                              </option>
                              <option key="birthday" value="birthday">
                                {t("passes.form.layoutOrder.birthday")}
                              </option>
                              {formData.type === "stamp" && (
                                <>
                                  <option key="rewards" value="rewards">
                                    {t("passes.form.layoutOrder.rewards")}
                                  </option>
                                  <option key="stamps" value="stamps">
                                    {t("passes.form.layoutOrder.stamps")}
                                  </option>
                                </>
                              )}
                              {formData.enableTier && (
                                <option key="tier" value="tier">
                                  {t("passes.form.layoutOrder.tier")}
                                </option>
                              )}
                              {formData.type === "discount" && (
                                <option key="discount" value="discount">
                                  {t("passes.form.layoutOrder.discount")}
                                </option>
                              )}
                              {formData.type === "cashback" && (
                                <option key="points" value="points">
                                  {t("passes.form.layoutOrder.points")}
                                </option>
                              )}
                              {formData.type === "balance" && (
                                <option key="balance" value="balance">
                                  {t("passes.form.layoutOrder.balance")}
                                </option>
                              )}
                              {formData.type === "cashback_money" && (
                                <option
                                  key="balance_money"
                                  value="balance_money"
                                >
                                  {t("passes.form.layoutOrder.balance_money")}
                                </option>
                              )}
                              {formData.type === "membership" && (
                                <option key="visits" value="visits">
                                  {t("passes.form.layoutOrder.visits")}
                                </option>
                              )}
                              {formData.enablePassExpiration && (
                                <option key="valid_until" value="valid_until">
                                  {t("passes.form.layoutOrder.validUntil")}
                                </option>
                              )}
                              {selectedStore?.custom_fields?.map((field) => (
                                <option key={field.id} value={field.label}>
                                  {field.label.toUpperCase()}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tier */}
                  {["cashback", "discount", "stamp"].includes(formData.type) && (
                    <div className="border border-gray-300 rounded-md p-4 mb-4">
                      <div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="tierEnabled"
                            checked={formData.enableTier}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                enableTier: e.target.checked,
                                // Clearing the target is what actually turns
                                // tiers off: the upgrade campaign is built from
                                // target_pass_id, and the backend only destroys
                                // it when that reads "none". Leaving the link
                                // behind hid the fields while customers kept
                                // being promoted by a setting the merchant
                                // believed they had switched off.
                                ...(e.target.checked
                                  ? {}
                                  : { targetPassId: "none", targetAmount: "" }),
                              }))
                            }
                            className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label
                            htmlFor="tierEnabled"
                            className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
                          >
                            {t("passes.form.enableTier")}
                          </label>
                        </div>
                      </div>

                      {formData.enableTier && (
                        <div>
                          <div className="sm:flex sm:gap-4 mt-4">
                            <div className="flex-1 mb-4 sm:mb-0">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {t("passes.form.tier.label")}
                                </label>
                              </div>
                              <input
                                type="text"
                                value={formData.tier.label}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    tier: {
                                      ...prev.tier,
                                      label: e.target.value,
                                    },
                                  }));
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {t("passes.form.tier.value")}
                                </label>
                              </div>
                              <input
                                type="text"
                                value={formData.tier.value}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    tier: {
                                      ...prev.tier,
                                      value: e.target.value,
                                    },
                                  }));
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                              />
                            </div>
                          </div>
                          <div className="sm:flex sm:gap-4 mt-4 space-y-4 sm:space-y-0">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {t("passes.form.tier.targetPass")}
                                </label>
                                {/* The ladder is the chain of "next card"
                                    links, so pointing the last tier back at
                                    the first is what turns it into a loop —
                                    there is no separate setting for it. Say so
                                    here, or the merchant has no way to
                                    discover it. */}
                                {formData.type == "stamp" && (
                                  <Tooltip
                                    text={t("passes.form.tier.stampTargetHint")}
                                    className="ml-1"
                                  />
                                )}
                              </div>
                              <select
                                value={formData.targetPassId}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    targetPassId: e.target.value,
                                    // A blank threshold is not a valid campaign:
                                    // Campaign validates `point` for presence, so
                                    // saving a target with no number behind it
                                    // raises out of the pass update. Seed the
                                    // smallest meaningful value — one completed
                                    // card — so the field is never empty while a
                                    // target is selected.
                                    targetAmount:
                                      e.target.value == "none"
                                        ? ""
                                        : prev.targetAmount ||
                                          (prev.type == "stamp" ? 1 : ""),
                                  }))
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                              >
                                <option value="none">
                                  {t("passes.form.tier.targetPassNone")}
                                </option>
                                {passes
                                  ?.filter(
                                    (pass) =>
                                      pass.pass.type == formData.type &&
                                      pass.id != formData.id &&
                                      ["discount", "cashback", "stamp"].includes(
                                        pass.pass.type,
                                      ),
                                  )
                                  .map((pass) => (
                                    <option key={pass.id} value={pass.id}>
                                      {pass.pass.name}
                                    </option>
                                  ))}
                              </select>
                            </div>

                            {/* No mt-* here: the label rows are the same
                                height, so the select and the input line up on
                                their own. A nudge on one column only pushed
                                this field half a row below its neighbour. */}
                            <div className="flex-1">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {formData.type == "discount"
                                    ? t("passes.form.tier.targetDiscount")
                                    : formData.type == "stamp"
                                      ? t("passes.form.tier.targetStampCards")
                                      : t("passes.form.tier.targetCashback")}
                                </label>
                                {formData.type == "stamp" && (
                                  <Tooltip
                                    text={t(
                                      "passes.form.tier.targetStampCardsTooltip",
                                    )}
                                    className="ml-1"
                                  />
                                )}
                              </div>
                              <input
                                type="number"
                                min={formData.type == "stamp" ? 1 : undefined}
                                step={formData.type == "stamp" ? 1 : undefined}
                                disabled={formData.targetPassId == "none"}
                                value={formData.targetAmount}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    targetAmount: e.target.value,
                                  }))
                                }
                                // Seeding on target selection is not enough:
                                // the merchant can still clear the field
                                // afterwards, and the backend rejects a next
                                // tier with no threshold. min/step only hint —
                                // save is a button handler, not a form submit.
                                // Restore the seed when they leave it empty.
                                onBlur={(e) => {
                                  if (
                                    formData.type == "stamp" &&
                                    formData.targetPassId != "none" &&
                                    e.target.value === ""
                                  ) {
                                    setFormData((prev) => ({
                                      ...prev,
                                      targetAmount: 1,
                                    }));
                                  }
                                }}
                                className={`mt-1 block w-full rounded-md shadow-xs ${
                                  tierThresholdError
                                    ? "border-red-500"
                                    : "border-gray-300"
                                }`}
                              />
                              {tierThresholdError && (
                                <p className="mt-1 text-xs text-red-600">
                                  {t("passes.form.tier.targetAmountRequired")}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Referal Program */}
                  {["stamp", "balance", "cashback", "cashback_money"].includes(
                    formData.type,
                  ) && (
                    <div className="mb-4">
                      <div className="border border-gray-300 rounded-md p-4 mb-4">
                        <div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="enableReferalProgram"
                              checked={formData.enableReferalProgram}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  enableReferalProgram: e.target.checked,
                                }))
                              }
                              className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <label
                              htmlFor="enableReferalProgram"
                              className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
                            >
                              {t("passes.form.enableReferalProgram")}
                            </label>
                            <Tooltip
                              text={t(
                                "passes.form.enableReferalProgramTooltip",
                              )}
                              className="ml-1"
                            />
                          </div>
                        </div>

                        {formData.enableReferalProgram && (
                          <div className="sm:flex sm:gap-4 mt-4 space-y-4 sm:space-y-0">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <label className="block text-sm font-medium text-gray-700">
                                  {t("passes.form.referralProgram.type")}
                                </label>
                                <Tooltip
                                  text={t(
                                    "passes.form.referralProgram.typeTooltip",
                                  )}
                                  className="ml-1"
                                />
                              </div>
                              <select
                                value={formData.referralProgram.type}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    referralProgram: {
                                      ...prev.referralProgram,
                                      type: e.target.value,
                                    },
                                  }))
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                              >
                                <option value="register">
                                  {t("passes.form.referralProgram.register")}
                                </option>
                                <option value="use">
                                  {t("passes.form.referralProgram.use")}
                                </option>
                              </select>
                            </div>
                            <div className="flex-1">
                              {formData.type === "stamp" && (
                                <>
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {t(
                                        "passes.form.referralProgram.referrerStamps",
                                      )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.referralProgram.referrerStampsTooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>

                                  <NumberInput
                                    value={
                                      formData.referralProgram.referrerPoints
                                    }
                                    className="mt-1"
                                    onChange={(value) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        referralProgram: {
                                          ...prev.referralProgram,
                                          referrerPoints: value,
                                        },
                                      }))
                                    }
                                    min={0}
                                    max={18}
                                  />
                                </>
                              )}
                              {formData.type === "cashback" && (
                                <>
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {t(
                                        "passes.form.referralProgram.referrerPoints",
                                      )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.referralProgram.referrerPointsTooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>
                                  <input
                                    type="number"
                                    value={
                                      formData.referralProgram.referrerPoints
                                    }
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        referralProgram: {
                                          ...prev.referralProgram,
                                          referrerPoints: e.target.value,
                                        },
                                      }))
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </>
                              )}
                              {formData.type === "balance" && (
                                <>
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {t(
                                        "passes.form.referralProgram.referrerCredits",
                                      )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.referralProgram.referrerCreditsTooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>
                                  <input
                                    type="number"
                                    value={
                                      formData.referralProgram.referrerPoints
                                    }
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        referralProgram: {
                                          ...prev.referralProgram,
                                          referrerPoints: e.target.value,
                                        },
                                      }))
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </>
                              )}
                              {formData.type === "cashback_money" && (
                                <>
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {t(
                                        "passes.form.referralProgram.referrerMoney",
                                      )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.referralProgram.referrerMoneyTooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>
                                  <input
                                    type="number"
                                    value={
                                      formData.referralProgram.referrerPoints
                                    }
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        referralProgram: {
                                          ...prev.referralProgram,
                                          referrerPoints: e.target.value,
                                        },
                                      }))
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </>
                              )}
                            </div>
                            <div className="flex-1">
                              {formData.type === "stamp" && (
                                <>
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {t(
                                        "passes.form.referralProgram.refereeStamps",
                                      )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.referralProgram.refereeStampsTooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>
                                  <NumberInput
                                    value={
                                      formData.referralProgram.refereePoints
                                    }
                                    className="mt-1"
                                    onChange={(value) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        referralProgram: {
                                          ...prev.referralProgram,
                                          refereePoints: value,
                                        },
                                      }))
                                    }
                                    min={0}
                                    max={18}
                                  />
                                </>
                              )}
                              {formData.type === "cashback" && (
                                <>
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {t(
                                        "passes.form.referralProgram.refereePoints",
                                      )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.referralProgram.refereePointsTooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>
                                  <input
                                    type="number"
                                    value={
                                      formData.referralProgram.refereePoints
                                    }
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        referralProgram: {
                                          ...prev.referralProgram,
                                          refereePoints: e.target.value,
                                        },
                                      }))
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </>
                              )}
                              {formData.type === "balance" && (
                                <>
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {t(
                                        "passes.form.referralProgram.refereeCredits",
                                      )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.referralProgram.refereeCreditsTooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>
                                  <input
                                    type="number"
                                    value={
                                      formData.referralProgram.refereePoints
                                    }
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        referralProgram: {
                                          ...prev.referralProgram,
                                          refereePoints: e.target.value,
                                        },
                                      }))
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </>
                              )}
                              {formData.type === "cashback_money" && (
                                <>
                                  <div className="flex items-center">
                                    <label className="block text-sm font-medium text-gray-700">
                                      {t(
                                        "passes.form.referralProgram.refereeMoney",
                                      )}
                                    </label>
                                    <Tooltip
                                      text={t(
                                        "passes.form.referralProgram.refereeMoneyTooltip",
                                      )}
                                      className="ml-1"
                                    />
                                  </div>
                                  <input
                                    type="number"
                                    value={
                                      formData.referralProgram.refereePoints
                                    }
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        referralProgram: {
                                          ...prev.referralProgram,
                                          refereePoints: e.target.value,
                                        },
                                      }))
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Review & Feedback Link` */}
                  <div className="mb-4">
                    <div className="border border-gray-300 rounded-md p-4 mb-4">
                      <div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="enableReviewFeedbackLink"
                            checked={formData.enableReviewFeedbackLink}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                enableReviewFeedbackLink: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label
                            htmlFor="enableReviewFeedbackLink"
                            className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
                          >
                            {t("passes.form.enableReviewFeedbackLink")}
                          </label>
                          <Tooltip
                            text={t(
                              "passes.form.enableReviewFeedbackLinkTooltip",
                            )}
                            className="ml-1"
                          />
                        </div>
                      </div>

                      {formData.enableReviewFeedbackLink && (
                        <div className="sm:flex sm:gap-4 mt-4 space-y-4 sm:space-y-0">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <label className="block text-sm font-medium text-gray-700">
                                {t("passes.form.reviewFeedbackLink.label")}
                              </label>
                            </div>
                            <input
                              type="text"
                              placeholder="Review & Feedback"
                              value={formData.reviewFeedback.label}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  reviewFeedback: {
                                    ...prev.reviewFeedback,
                                    label: e.target.value,
                                  },
                                }))
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                            />
                          </div>
                          <div className="flex-2">
                            <div className="flex items-center">
                              <label className="block text-sm font-medium text-gray-700">
                                {t("passes.form.reviewFeedbackLink.url")}
                              </label>
                            </div>
                            <input
                              type="text"
                              placeholder="https://maps.google.com/..."
                              value={formData.reviewFeedback.url}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  reviewFeedback: {
                                    ...prev.reviewFeedback,
                                    url: e.target.value,
                                  },
                                }))
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Links */}
                  <div className="mb-4">
                    <div className="border border-gray-300 rounded-md p-4 mb-4">
                      <div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="enableLinks"
                            checked={formData.enableLinks}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                enableLinks: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label
                            htmlFor="enableLinks"
                            className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
                          >
                            {t("passes.form.enableLinks")}
                          </label>
                          <Tooltip
                            text={t("passes.form.enableLinksTooltip")}
                            className="ml-1"
                          />
                        </div>
                      </div>

                      {formData.enableLinks && (
                        <>
                          {formData.links.map((link, index) => (
                            <div className="sm:flex sm:gap-4 mt-4 space-y-4 sm:space-y-0 pb-4 border border-gray-300 rounded-md p-4 mb-4">
                              <div className="flex-1">
                                <div className="flex items-center">
                                  <label className="block text-sm font-medium text-gray-700">
                                    {t("passes.form.links.label")}
                                  </label>
                                </div>
                                <input
                                  type="text"
                                  placeholder=""
                                  value={link.label}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      links: prev.links.map((l, i) =>
                                        i === index
                                          ? { ...l, label: e.target.value }
                                          : l,
                                      ),
                                    }))
                                  }
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                />
                              </div>
                              <div className="flex-2">
                                <div className="flex items-center">
                                  <label className="block text-sm font-medium text-gray-700">
                                    {t("passes.form.links.url")}
                                  </label>
                                </div>
                                <input
                                  type="text"
                                  value={link.url}
                                  placeholder="https://..."
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      links: prev.links.map((l, i) =>
                                        i === index
                                          ? { ...l, url: e.target.value }
                                          : l,
                                      ),
                                    }))
                                  }
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                />
                              </div>
                              <div className="flex items-end pb-2">
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center text-red-500 rounded-full p-0 sm:w-8 sm:h-8 py-1 sm:px-0 sm:py-0"
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      links: prev.links.filter(
                                        (_, i) => i !== index,
                                      ),
                                    }))
                                  }
                                >
                                  <TrashIcon className="w-6 h-6 sm:mr-0 mr-2" />
                                  <span className="sm:hidden">
                                    {t("passes.form.links.remove")}
                                  </span>
                                </button>
                              </div>
                            </div>
                          ))}
                          <div className="mt-4">
                            <button
                              type="button"
                              className="inline-flex items-center justify-center text-indigo-500 rounded-full p-0"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  links: [
                                    ...prev.links,
                                    { title: "", link: "" },
                                  ],
                                }))
                              }
                            >
                              <PlusCircleIcon className="w-6 h-6 mr-2" />
                              {t("passes.form.links.add")}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Custom Back Fields */}
                  <div className="mb-4">
                    <div className="border border-gray-300 rounded-md p-4 mb-4">
                      <div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="enableCustomBackFields"
                            checked={formData.enableCustomBackFields}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                enableCustomBackFields: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label
                            htmlFor="enableCustomBackFields"
                            className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
                          >
                            {t("passes.form.customBackFields.enable")}
                          </label>
                          <Tooltip
                            text={t(
                              "passes.form.customBackFields.enableTooltip",
                            )}
                            className="ml-1"
                          />
                        </div>
                      </div>

                      {formData.enableCustomBackFields && (
                        <>
                          {(formData.customBackFields || []).map(
                            (field, index) => (
                              <div
                                key={index}
                                className="mt-4 space-y-4 pb-4 border border-gray-300 rounded-md p-4 mb-4"
                              >
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    {t("passes.form.customBackFields.label")}
                                  </label>
                                  <input
                                    type="text"
                                    value={field.label}
                                    placeholder={t(
                                      "passes.form.customBackFields.labelPlaceholder",
                                    )}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        customBackFields:
                                          prev.customBackFields.map((f, i) =>
                                            i === index
                                              ? { ...f, label: e.target.value }
                                              : f,
                                          ),
                                      }))
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    {t("passes.form.customBackFields.value")}
                                  </label>
                                  <textarea
                                    rows={4}
                                    value={field.value}
                                    placeholder={t(
                                      "passes.form.customBackFields.valuePlaceholder",
                                    )}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        customBackFields:
                                          prev.customBackFields.map((f, i) =>
                                            i === index
                                              ? { ...f, value: e.target.value }
                                              : f,
                                          ),
                                      }))
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                                  />
                                </div>
                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    className="inline-flex items-center text-red-500"
                                    onClick={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        customBackFields:
                                          prev.customBackFields.filter(
                                            (_, i) => i !== index,
                                          ),
                                      }))
                                    }
                                  >
                                    <TrashIcon className="w-6 h-6 mr-2" />
                                    {t("passes.form.customBackFields.remove")}
                                  </button>
                                </div>
                              </div>
                            ),
                          )}
                          <div className="mt-4">
                            <button
                              type="button"
                              className="inline-flex items-center justify-center text-indigo-500 rounded-full p-0"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  customBackFields: [
                                    ...(prev.customBackFields || []),
                                    { label: "", value: "" },
                                  ],
                                }))
                              }
                            >
                              <PlusCircleIcon className="w-6 h-6 mr-2" />
                              {t("passes.form.customBackFields.add")}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Terms of Service Section */}
                  <div className="border border-gray-300 rounded-md p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("passes.form.termsOfService.label")}
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsTermsModalOpen(true)}
                        className="text-sm text-indigo-600 flex items-center  hover:text-indigo-500 ml-2"
                      >
                        {t("passes.form.termsOfService.viewButton")}
                      </button>
                    </div>
                    <div className="mt-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="useCustomTerms"
                          checked={formData.useCustomTerms}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              useCustomTerms: e.target.checked,
                            }));
                            if (!e.target.checked) {
                              setTermsUrl("");
                            }
                          }}
                          className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label
                          htmlFor="useCustomTerms"
                          className="ml-2 items-center block text-sm text-gray-900"
                        >
                          {t("passes.form.termsOfService.useCustom")}
                        </label>
                      </div>
                      {formData.useCustomTerms && (
                        <>
                          <input
                            type="url"
                            value={formData.termsOfServiceUrl}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                termsOfServiceUrl: e.target.value,
                              }))
                            }
                            placeholder={t(
                              "passes.form.termsOfService.urlPlaceholder",
                            )}
                            className="mt-2 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {t("passes.form.termsOfService.customHint")}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Support */}
                  <div className="mb-4">
                    <div className="flex items-center">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("passes.form.support.label")}
                      </label>
                      <Tooltip
                        text={t("passes.form.support.tooltip")}
                        className="ml-1"
                      />
                    </div>
                    <input
                      type="email"
                      value={formData.supportEmail}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          supportEmail: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-xs"
                    />
                  </div>

                  {/* Barcode */}
                  <div className="mb-4">
                    <div className="flex items-center">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("passes.form.barcode.label")}
                      </label>
                      <Tooltip
                        text={t("passes.form.barcode.tooltip")}
                        className="ml-1"
                      />
                    </div>
                    <select
                      value={formData.barcodeType}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          barcodeType: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option key="none" value="none">
                        {t("passes.form.barcode.none")}
                      </option>
                      <option key="qr" value="qr">
                        {t("passes.form.barcode.qr")}
                      </option>
                      <option key="code128" value="code128">
                        {t("passes.form.barcode.code128")}
                      </option>
                      <option key="aztec" value="aztec">
                        {t("passes.form.barcode.aztec")}
                      </option>
                      <option key="pdf417" value="pdf417">
                        {t("passes.form.barcode.pdf417")}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end items-center pt-4">
              <button
                onClick={handleSaveLayout}
                disabled={isSaving}
                className={`px-4 py-2 text-white text-sm font-medium rounded-md ${
                  isSaving
                    ? "bg-indigo-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                <div className="flex items-center">
                  {isSaving && (
                    <div className="mr-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-b-transparent border-white"></div>
                    </div>
                  )}
                  {isSaving
                    ? t("common.saving")
                    : t(
                        isEditMode ? "passes.edit.button" : "passes.new.button",
                      )}
                </div>
              </button>
            </div>

            {/* Rest of the form content will be added in next steps */}
          </div>
        </div>
      </div>
    </div>
  );
}
